import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import ctaBg from "@/assets/cta-bg.webp";

interface CTABandProps {
  title?: string;
  subtitle?: string;
}

export function CTABand({ 
  title = "Ready to discuss your startup's technology strategy?",
  subtitle = "Book a discovery call with Founder Mitra. No commitment, just clarity on your next move."
}: CTABandProps) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="relative isolate py-28 md:py-36 overflow-hidden bg-[#0b0b0b]">
      {/* Cinematic parallax photograph */}
      <motion.div
        aria-hidden="true"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-20 will-change-transform"
      >
        <img
          src={ctaBg}
          alt=""
          width={1920}
          height={1080}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Dark tint */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,8,18,0.78) 0%, rgba(8,8,18,0.62) 50%, rgba(8,8,18,0.85) 100%)",
        }}
      />

      {/* Subtle grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />
      
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-3xl text-center relative z-10">
        <motion.p
          className="text-xs font-semibold uppercase tracking-[0.22em] text-accent mb-5"
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5 }}
        >
          Let's talk
        </motion.p>
        <motion.h2 
          className="text-3xl md:text-4xl lg:text-5xl font-display font-bold tracking-[-0.02em] leading-[1.1] text-white mb-6"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.7, delay: 0.05, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          {title}
        </motion.h2>
        <motion.p 
          className="text-white/75 mb-12 text-lg md:text-xl leading-[1.65] max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          {subtitle}
        </motion.p>
        <motion.div 
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : undefined}
          transition={{ duration: 0.5, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            to="/book-call"
            className="group inline-flex items-center gap-3 rounded-full bg-white text-[hsl(248_62%_21%)] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[hsl(248_62%_21%)] text-white transition-transform duration-300 group-hover:translate-x-0.5">
              <ArrowRight className="h-4 w-4" />
            </span>
            Book a Discovery Call
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-6 py-3 text-sm font-medium text-white hover:bg-white/20 transition-all duration-300"
          >
            Contact Us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
